import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  InfiniteScrollDropdownProps,
  InfiniteScrollDropdownSelection,
} from '~/components/InfiniteScrollDropdown/InfiniteScrollDropdown';
import { AudiencePageQueryAudience } from '~/constants/graphqlTypes';
import { useExtendedTranslation } from '~/hooks/useExtendedTranslation';
import { AudienceCondition, AudiencesPageQuery, OrganizationType, useAudiencesPageQuery } from '~/types/api.graphql';

import { DropdownItemProps } from '../Dropdown/Dropdown';
import { StyledInfiniteScrollDropdown } from './AudiencesDropdown.styles';

const PAGE_ITEMS_COUNT = 10;

export interface AudiencesDropdownProps
  extends Omit<InfiniteScrollDropdownProps<AudiencePageQueryAudience>, 'onSelectionChange'> {
  organizationType?: OrganizationType;
  isAudienceDisabled?: (audience: AudiencePageQueryAudience) => boolean;
  onSelectionChange?: (selection: InfiniteScrollDropdownSelection, audience: AudiencePageQueryAudience) => void;
  organizationId: string;
  conditions?: AudienceCondition[];
}

const AudiencesDropdown = (props: AudiencesDropdownProps) => {
  const { conditions, isAudienceDisabled, onSelectionChange, organizationId, selectedIndex: selectedIndexProp } = props;
  const { tSpecific } = useExtendedTranslation();
  const [items, setItems] = useState<DropdownItemProps<AudiencePageQueryAudience>[]>([]);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [searchOrgInfiniteScroll, setSearchOrgInfiniteScroll] = useState('');
  const [selectedIndex, setSelectedIndex] = useState<number>(props.selectedIndex);

  const disabledKeys = useMemo(() => {
    return items.filter((item) => item.disabled).map((item) => item.key);
  }, [items]);

  const applyAudiencesPage = useCallback(
    (data: AudiencesPageQuery) => {
      setItems((prevItems) => [
        ...prevItems,
        ...(data?.audiencesPage?.nodes?.map((audience) => ({
          text: audience.name,
          value: audience,
          description: tSpecific('audience.overview.'.concat(String(audience.condition).toLowerCase())),
          key: audience.id,
          disabled: isAudienceDisabled ? isAudienceDisabled(audience) : false,
        })) || []),
      ]);
      setHasMore(data?.audiencesPage?.pageInfo?.hasNextPage);
      setPage((prevPage: number) => prevPage + 1);
    },
    [tSpecific, isAudienceDisabled],
  );

  const {
    refetch,
    loading: loadingFirst,
    data: audiencesQueryData,
  } = useAudiencesPageQuery({
    variables: {
      pageNumber: page,
      pageItemsCount: PAGE_ITEMS_COUNT,
      searchQuery: searchOrgInfiniteScroll,
      organizationId,
      ...(conditions ? { conditions } : {}),
    },
    fetchPolicy: 'network-only',
    skip: page > 1 || !organizationId,
  });

  useEffect(() => {
    if (audiencesQueryData) {
      applyAudiencesPage(audiencesQueryData);
    }
    // Run once per Apollo data object. applyAudiencesPage changes can re-append the same page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audiencesQueryData]);

  useEffect(() => {
    setPage(1);
    setItems([]);
    setSelectedIndex(selectedIndexProp);
  }, [searchOrgInfiniteScroll, organizationId, selectedIndexProp]);

  const handleItemSelection = useCallback(
    (selection) => {
      const selectedIndex = selection as number;
      setSelectedIndex(selectedIndex);
      onSelectionChange?.(selectedIndex, items[selectedIndex].value);
    },
    [items, onSelectionChange],
  );

  useEffect(() => {
    if (items?.length === 1 && !items?.[0]?.disabled) {
      handleItemSelection(0);
    }
  }, [handleItemSelection, items, page]);

  const handleLoadMore = () => {
    void (async () => {
      setLoadingMore(true);
      const result = await refetch({
        pageNumber: page,
        pageItemsCount: PAGE_ITEMS_COUNT,
        searchQuery: searchOrgInfiniteScroll,
      });
      if (result.data) applyAudiencesPage(result.data);
      setLoadingMore(false);
    })();
  };

  return (
    <StyledInfiniteScrollDropdown
      {...props}
      items={items}
      disabledKeys={disabledKeys}
      onSelectionChange={handleItemSelection}
      selectedIndex={selectedIndex}
      setSearchOrgInfiniteScroll={setSearchOrgInfiniteScroll}
      searchOrgInfiniteScroll={searchOrgInfiniteScroll}
      hasMore={hasMore}
      loadMore={handleLoadMore}
      loadingFirst={loadingFirst}
      loadingMore={loadingMore}
      disallowEmptySelection
    />
  );
};

export default AudiencesDropdown;
